import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBusinessContext } from '../contexts/BusinessContext';
import MfaVerification from '../components/auth/MfaVerification';

const Login = () => {
  const navigate = useNavigate();
  const { login, completeMfaLogin } = useBusinessContext();

  // Redirect to dashboard if already logged in (helps PWA start_url)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setErrors({});

    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaRequired(true);
        setLoading(false);
        return;
      }
      
      const userRole = result?.user?.role || result?.role;
      if (userRole === 'ApplicationAdmin' || userRole === 'Support') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.message || 'Login failed';
      
      if (errorMessage.toLowerCase().includes('cancelled') || 
          errorMessage.toLowerCase().includes('canceled')) {
        navigate('/subscription-cancelled', {
          state: { 
            message: errorMessage,
            immediate: true 
          }
        });
        return;
      }
      
      if (errorMessage.includes('Trial period has ended') ||
          errorMessage.includes('trial has expired')) {
        navigate('/trial-expired', { 
          state: { message: errorMessage }
        });
        return;
      }
      
      if (errorMessage.includes('subscribe') ||
          errorMessage.includes('not active')) {
        navigate('/trial-expired', { 
          state: { message: errorMessage }
        });
        return;
      }
      
      setErrors({ 
        submit: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = async (authResponse) => {
    try {
      await completeMfaLogin(authResponse);
      
      const userRole = authResponse?.role;
      if (userRole === 'ApplicationAdmin' || userRole === 'Support') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      setErrors({ submit: error.message || 'MFA verification failed' });
      setMfaRequired(false);
      setMfaToken(null);
    }
  };

  const handleMfaCancel = () => {
    setMfaRequired(false);
    setMfaToken(null);
    setFormData(prev => ({ ...prev, password: '' }));
  };

  if (mfaRequired) {
    return (
      <MfaVerification
        mfaToken={mfaToken}
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800">
      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo inside card */}
          <div className="text-center mb-8">
            <img 
              src="/images/SuddyFlow-Logo-PNG.png" 
              alt="SuddyFlow" 
              className="h-10 mx-auto mb-6"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
                Sign up free
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <span className="text-red-500">⚠️</span>
                <span>{errors.submit}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="you@example.com"
                autoComplete="email"
              />
              {errors.email && <p className="mt-2 text-sm text-red-600 font-medium">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
                placeholder="••••••••"
                autoComplete="current-password"
              />
              {errors.password && <p className="mt-2 text-sm text-red-600 font-medium">{errors.password}</p>}
            </div>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3.5 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white text-sm flex items-center justify-center gap-2">
            <span className="bg-white/20 px-2 py-1 rounded font-semibold text-xs backdrop-blur-sm">🇺🇸 VETERAN-OWNED</span>
            <span>•</span>
            <span>Trusted by Service Professionals</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;